import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Table, Button, Form, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';

const TemplateCRUD = () => {
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [templateTypes, setTemplateTypes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState({ title: '', id: null, types: [], image: null });
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTemplateType, setSelectedTemplateType] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [variant, setVariant] = useState('success');
  const [selectedImage, setSelectedImage] = useState(null);

  const imagepath = 'https://drdprints.com/uploads/templates/';

  useEffect(() => {
    fetchTemplates();
    fetchCategories();
    fetchTemplateTypes();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await axios.get('https://drdprints.com/api/get-templates');
      setTemplates(response.data.templates || []);
    } catch (error) {
      toast.error('Error fetching templates.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get('https://drdprints.com/api/get-category');
      setCategories(response.data.categories || []);
    } catch (error) {
      toast.error('Error fetching categories.');
    }
  };

  const fetchTemplateTypes = async () => {
    try {
      const response = await axios.get('https://drdprints.com/api/get-templatetypes');
      setTemplateTypes(response.data.template_types || []);
    } catch (error) {
      toast.error('Error fetching template types.');
    }
  };

  const handleShowModal = (template = { title: '', id: null, types: [], image: null }) => {
    setCurrentTemplate(template);
    setSelectedCategory(template.cat_id || '');
    setSelectedTemplateType(template.template_type_id || '');
    setSelectedImage(null); 
    setIsEditMode(!!template.id);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setCurrentTemplate({ title: '', id: null, types: [], image: null });
    setSelectedCategory('');
    setSelectedTemplateType('');
    setSelectedImage(null);
  };

  const handleInputChange = (e) => {
    setCurrentTemplate({ ...currentTemplate, [e.target.name]: e.target.value });
  };

  const handleImageChange = (e) => {
    setSelectedImage(e.target.files[0]);
  };

  const handleSaveTemplate = async () => {
    try {
      if (!selectedCategory) {
        toast.error('Please select a category.');
        return;
      }

      if (!currentTemplate.title) {
        toast.error('Title is required.');
        return;
      }

      const formData = new FormData();
      formData.append('title', currentTemplate.title);
      formData.append('cat_id', parseInt(selectedCategory));
      formData.append('template_type_id', selectedTemplateType);

      if (selectedImage) {
        formData.append('image', selectedImage);
      }

      if (isEditMode) {
        await axios.put(
          `https://drdprints.com/api/update-template/${currentTemplate.id}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        toast.success('Template updated successfully.');
      } else {
        await axios.post('https://drdprints.com/api/store-template', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Template created successfully.');
      }

      fetchTemplates();
      handleCloseModal();
    } catch (error) {
      toast.error('Error saving template.');
    }
  };

  const handleDeleteTemplate = async (id) => {
    try {
      await axios.delete(`https://drdprints.com/api/deletetemplate/${id}`);
      fetchTemplates();
    } catch (error) {
      toast.error('Error deleting template.');
    }
  };

  const getCategoryNameById = (catId) => {
    const category = categories.find(cat => cat.id === catId);
    return category ? category.cat_title : 'No category';
  };

  const getTemplateTypeNameById = (typeId) => {
    const type = templateTypes.find(t => t.id === typeId);
    return type ? type.title : 'No type';
  };

  const getImageUrl = (imageName) => {
    return imageName ? `${imagepath}${imageName}` : null;
  };

  return (
    <div className="container">
      <h1>Template Management</h1>
      <Button onClick={() => handleShowModal()}>Add New Template</Button>

      {loading && <p>Loading templates...</p>}
      {message && <p className={`text-${variant}`}>{message}</p>}

      <Table striped bordered hover className="mt-4">
        <thead>
          <tr>
            <th>ID</th>
            <th>Title</th>
            <th>Category</th>
            <th>Template Type</th>
            <th>Image</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {templates.map((template) => (
            <tr key={template.id}>
              <td>{template.id}</td>
              <td>{template.template_name}</td>
              <td>{getCategoryNameById(template.cat_id)}</td>
              <td>{getTemplateTypeNameById(template.template_type_id)}</td>
              <td>
                {template.image ? (
                  <img src={getImageUrl(template.image)} alt="Template" width="100" />
                ) : 'No image'}
              </td>
              <td>
                <Button variant="warning" onClick={() => handleShowModal(template)}>
                  Edit
                </Button>{' '}
                <Button variant="danger" onClick={() => handleDeleteTemplate(template.id)}>
                  Delete
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{isEditMode ? 'Edit Template' : 'Add Template'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="formTitle">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter template title"
                name="title"
                value={currentTemplate.title}
                onChange={handleInputChange}
              />
            </Form.Group>

            <Form.Group controlId="formCategorySelect">
              <Form.Label>Select Category</Form.Label>
              <Form.Control
                as="select"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="">Select a category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.cat_title}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group controlId="formTemplateTypeSelect">
              <Form.Label>Select Template Type</Form.Label>
              <Form.Control
                as="select"
                value={selectedTemplateType}
                onChange={(e) => setSelectedTemplateType(e.target.value)}
              >
                <option value="">Select a template type</option>
                {templateTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.title}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>

            <Form.Group controlId="formImage">
              <Form.Label>Upload Image</Form.Label>
              <Form.Control type="file" onChange={handleImageChange} />
              {currentTemplate.image && !selectedImage && (
                <div className="mt-2">
                  <img src={getImageUrl(currentTemplate.image)} alt="Current" width="100" />
                  <p>Current Image</p>
                </div>
              )}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Close
          </Button>
          <Button variant="primary" onClick={handleSaveTemplate}>
            {isEditMode ? 'Update' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TemplateCRUD;
